import { SliceableHelper } from '~src/httpClient/utils/SliceableHelper';
import { UserNames } from '~src/userManagement/resources/UserNames';
import Users from '~src/userManagement/services/resources/Users';
import { IUserName } from '~src/userManagement/users/serverApi/types/IUser';

import { ListResult } from '../../httpClient/fetchers/ListResult';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { ITypedId } from '../../httpClient/serverApi/types/ITypedId';
import { ResultHelper } from '../../httpClient/utils/ResultHelper';
import WebHttpClient from '../../httpClient/WebHttpClient';
import { IGranteePermissions } from '../types/IGranteePermissions';
import { ITypedIds } from '../types/ITypedIds';
import { PermissionsHelper } from '../utils/PermissionsHelper';
import { PermissionsMerger } from '../utils/PermissionsMerger';
import { IGetExplicitPermissionsResult, ISourceAuthorizable } from './types/GetExplicitPermissionsRequest';
import { ICapabilityValue } from './types/ICapabilityValue';

export interface ExplicitPermissionsResult extends IResult {
  permissionsList: ListResult<IGranteePermissions>;
  sourceAuthorizablesById: _.Dictionary<ISourceAuthorizable>;
}

export function getUsersCanBeProjectLeader(userIds: string[]): Promise<ListResult<IUserName>> {
    return SliceableHelper.fetchAll(UserNames.all()
      .addFilter(Users.getByUserIdsFilter(userIds))
      .addFilter(Users.getCanOwnProjectFilter()));
}

export function getExplicitPermissionsForAuthorizables(
  authorizables: ITypedIds,
  checkProjectLeader: boolean
): Promise<ExplicitPermissionsResult> {
  const requests = PermissionsHelper.explicitPermissionsRequestsForAuthorizables(authorizables);
  const combineResultsFn = (results: IGetExplicitPermissionsResult[]) => {
    return {
      permissions: ResultHelper.combine(results, (result) => result.permissions),
      groups: ResultHelper.combine(results, (result) => result.groups),
      users: ResultHelper.combine(results, (result) => result.users),
      sourceAuthorizableById: ResultHelper.combineByKey(results, (result) => result.sourceAuthorizableById),
      errors: ResultHelper.combineErrors(results)
    };
  };

  return WebHttpClient.sendRequestsParallel(requests, combineResultsFn)
    .then<ExplicitPermissionsResult>((result: IGetExplicitPermissionsResult) => {
      if (checkProjectLeader) {
        const userIds = result.users.map((user) => user.id);
        getUsersCanBeProjectLeader(userIds)
        .then((usersCanBeProjectLeader: ListResult<IUserName>) => {
          const list = PermissionsMerger.makeGranteePermissionsList(
            result.permissions, authorizables.ids, result.groups, result.users, usersCanBeProjectLeader.items
          );
          return {
            permissionsList: new ListResult<IGranteePermissions>(list),
            sourceAuthorizablesById: result.sourceAuthorizableById,
            errors: result.errors
          };
        });
      }
      const list = PermissionsMerger.makeGranteePermissionsList(
        result.permissions, authorizables.ids, result.groups, result.users
      );
      return {
        permissionsList: new ListResult<IGranteePermissions>(list),
        sourceAuthorizablesById: result.sourceAuthorizableById,
        errors: result.errors
      };
  });
}

// Assign permissions to the same grantee and multiple authorizables
export function setExplicitPermissionsForAuthorizables(
  authorizables: ITypedIds,
  granteePermissions: IGranteePermissions,
): Promise<IResult> {
  const requests = PermissionsHelper.setExplicitPermissionsRequestsForAuthorizables(authorizables, granteePermissions);

  return WebHttpClient.sendRequestsParallel(requests, ResultHelper.combineResults);
}

// Assign permissions to the same authorizable and multiple grantees
export function setExplicitPermissionsForGrantees(
  authorizable: ITypedId,
  grantees: ITypedId[],
  permissions: ICapabilityValue[]
): Promise<IResult> {
  const requests = PermissionsHelper.setExplicitPermissionsRequestsForGrantees(authorizable, grantees, permissions);

  return WebHttpClient.sendRequestsParallel(requests, ResultHelper.combineResults);
}
